<template>
  <div class="is-section" data-test="ti-insight-section">
    <OutcomeRingCard :title="title" :subtitle="subtitle" :chart-data="team ? team.chartData : null" :chips="chips" :stats="stats" :stats-note="statsNote" :goal="goal" />
    <MemberBarsCard
      v-if="hasMembers && memberView === 'outcomes'"
      :members="members"
      :team-chart-data="team ? team.chartData : null"
      :subtitle="`${subtitle} by outcome, one shared scale`"
    />
    <div v-else-if="hasMembers" class="is-members" data-test="ti-section-members">
      <TrendBarsCard
        v-for="(m, i) in members"
        :key="`${m.name}-${i}`"
        :name="m.name"
        :total="m.totalCalls"
        :chart-data="m.chartData"
        :time-range="timeRange"
        :stats="m.stats || []"
      />
    </div>
  </div>
</template>

<script>
import OutcomeRingCard from './OutcomeRingCard.vue';
import TrendBarsCard from './TrendBarsCard.vue';
import MemberBarsCard from './MemberBarsCard.vue';

/**
 * One report section on Today / Week-to-Date / Month-to-Date / Custom Reports (Overdue Recall, Unscheduled Treatment, Outstanding Claims,
 * Broken Appointment, Pre-authorizations, Patient Balances) in the redesigned charts (gate 'teamInsightsCharts', redesign/gate.js): the team
 * card on the left (outcomes, ring, the follow-up and scheduled lists, the goal when the site has one) and the team members on the right —
 * one trend card each (Week, Month, Custom Reports) or one outcome bar each (Today, `memberView="outcomes"`). Fed the same data the classic
 * pie and bar charts use.
 */
export default {
  name: 'InsightSection',
  components: { OutcomeRingCard, TrendBarsCard, MemberBarsCard },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    // { totalCalls, chartData } — e.g. overdueRecallDataMonthly
    team: { type: Object, default: null },
    // [{ name, totalCalls, chartData, stats }] — e.g. overdueRecallDataMonthlyByEmp through chipRows.js buildMemberStats
    members: { type: Array, default: () => [] },
    // the lists the team tiles open, see chipRows.js
    chips: { type: Array, default: () => [] },
    // the team's four numbers, see chipRows.js buildStats
    stats: { type: Array, default: () => [] },
    // see chipRows.js buildUnassignedNote
    statsNote: { type: String, default: '' },
    timeRange: { type: String, default: 'month' },
    // 'trend' = a bar chart per person; 'outcomes' = one bar per person split by outcome (Today)
    memberView: { type: String, default: 'trend' },
    // { word: 'Daily', target, percent: '40', fill } — sectionHelpers.js tiGoal; null when the site has no goal for this section
    goal: { type: Object, default: null },
  },
  computed: {
    hasMembers() {
      return Array.isArray(this.members) && this.members.length > 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.is-section {
  display: grid;
  grid-template-columns: minmax(0, 440px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.is-members {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: 16px;
  min-width: 0;
}

@media (max-width: 880px) {
  .is-section { grid-template-columns: minmax(0, 1fr); }
}
</style>
